import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider, LoadingProgress } from '@framework-controls/core';
import Main from 'src/components/Main/containers/Main';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { useHistory } from 'react-router';
import { Customer, RecallData, SpecData, UpdateApptCustInp, ServiceDocsData, MCData } from 'src/models';
import * as themeBase from 'src/css/theme-base/_index.module.scss';
import { ViewMode } from 'src/enums';
import { setLocale, strings } from 'src/localization';
import { GetCookie, SetCookie } from '@reynolds/cookiemanager';
import * as css from './App.module.scss';
import { checkSpecTheme, getFavIconPath } from 'src/helpers/OEMHelper/OEMHelper';
import { ErrorBoundary } from '@reynolds/error-boundary';
import { version, name } from '../../../package.json';
import { URLHelper } from 'src/helpers';
import { IAppointmentState } from 'src/store/states';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import TagManager from 'react-gtm-module';
import ConnectedVehicle from '../ConnectedVehicle/containers/ConnectedVehicle';
import { IView } from 'Framework';
import { ComponentMapIds } from '../map';
import * as Sentry from '@sentry/react';
import { setProductName } from 'src/helpers/EventHelper/EventHelper';
import { getProviderId, sendSdEvent } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

export interface IProps {
    initialLoadDone: boolean;
    token: string;
    specData: SpecData | undefined;
    specProfile: string;
    theme: string;
    store: string;
    branch: string;
    tokenErrMsg: string;
    custInfo?: Customer;
    appointment: IAppointmentState;
    serviceDocsSpecs?: ServiceDocsData;
    recallData: RecallData;
    inquiryInvoked: boolean;
    waitOnRecall: boolean;
    eraError: string;
    logId?: string;
    countryCode: string;
    languageCode: string;
    locale: string;
    tlmId: string;
    serviceDocsFlag: string;
    osdLaunched: string;
    viewServiceHistFlag: string;
    portalLaunched: string;
    mainPaneViewOrder: IView[];
    isCookiesEnabled: boolean;
    custError: string | undefined;
    decodeErr: string | undefined;
    servicesError: string;
    recallError: string | undefined;
    quickTimesError: string | undefined;
    rmTimesError: string | undefined;
    menuError: string;
    vehError: string | undefined;
    serviceDocsError: string | undefined;
    webOpsError: string | undefined;
    frameworkError: string;
    frameworkTealiumAccount: string;
    loggingCustOff: boolean;
    repairOrderError?: string;
    roHistoryError?: string;
    waitOnMC: boolean;
    MCData: MCData;
    desktopOrMobile: string;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    vwDeepLinkMileage: string;
    vwVariant: string;
    vwKeyword: string;
    vwProvider: string;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    updateTheme: (varTheme: string) => void;
    getSpecs: (specProfile, store, branch) => void;
    setViewMode: (viewMode: ViewMode) => void;
    getTokenData: (token: string, localURL: boolean) => void;
    setPreviewFlag: (inp: string) => void;
    setAddlTrackData: (token: string, vin: string, year: string, make: string, model: string) => void;
    clearApptState: () => void;
    clearMenusState: () => void;
    clearNewVehicle: () => void;
    clearROHistory: () => void;
    selectVehicle: (id: string) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    onCustUpdate: (customer: Customer) => void;
    clearAdditionalServices: () => void;
    getServiceDocsSpecs: (store: string, branch: string) => void;
    clearVehicles: () => void;
    clearMainPanes: () => void;
    clearCheckedWebOps: () => void;
    clearAddlTrackCustInfo: () => void;
    setWaitOnRecall: (value: boolean) => void;
    setNoAutoRecall: (value: boolean) => void;
    setDesktopOrMobile: (value: string) => void;
    setPreviousToken: (value: string) => void;
    toggleLocale: (locale: string) => void;
    clearRO: () => void;
    waitForLogOff: () => void;
    logCustOff: () => void;
    setWaitOnMC: (value: boolean) => void;
    clearMaintContractData: () => void;
    pushToMainPanes: (inputView: IView) => void;
    setVwDeepLinkMileage: (value: string) => void;
    setVwDeepLinkTrackData: (variant: string, provider: string, keyword: string) => void;
}

const App = (props: IProps) => {
    const [fetchingSpecs, setFetchingSpecs] = useState(true);
    const [fetchingRecalls, setFetchingRecalls] = useState(false);
    const [loggingOffCust, setLoggingOffCust] = useState(false);
    const [tokenError, setTokenError] = useState(false);
    const [tokenErrorMsg, setTokenErrorMsg] = useState('');
    const [sysType, setSysType] = useState<'Prod' | 'QA'>('QA');
    const [apiUrl, setApiURL] = useState('');
    const [initLang, setInitLang] = useState<boolean>(false);
    const [specFlag, setSpecFlag] = useState<boolean>(false);
    const [haveToken, setHaveToken] = useState<boolean>(false);
    const [docSpecFlag, setDocSpecFlag] = useState<boolean>(false);
    const [loadApp, setLoadApp] = useState('none');
    const [fetchingMC, setFetchingMC] = useState(false);
    const loadedSDAnalytics = useRef(false);

    const isCookiesEnabled = props.isCookiesEnabled;
    const history = useHistory();
    const isPreview = new URLSearchParams(window.location.search).get('mode') === 'p' ? true : false;
    const brandFlags = [
        props.specData?.fordSmarttFlag,
        props.specData?.kiaTrackingFlag,
        // Add more brand tracking flags here in the future
    ];
    const hasAnyBrandFlag = brandFlags.some(Boolean);

    const vwDeepLinkParams = [
        props.vwDeepLinkMileage,
        props.vwVariant,
        props.vwKeyword,
        props.vwProvider,
    ];
    const vwDeepLinkData = vwDeepLinkParams.some(Boolean);

    function getSdConfig(
        specData: SpecData | undefined,
        toyotaMCLaunched: string,
        toyotaTDMCLaunched: string,
        lexusMCLaunched: string,
        lexusTDMCLaunched: string,
    ) {
        let siteType = '';
        let retailerId = '';
        let providerId = '';
        let cookieName = '';

        if (specData?.toyotaTrackingFlag === 'Y') {
            providerId =
                toyotaMCLaunched || toyotaTDMCLaunched ? 'SMARTPATHREYNOLDS' : 'REYNOLDS';
            siteType = 'Toyota';
            retailerId = specData.toyotaRetId;
            cookieName = '_sda:toyota:T3:session';
        } else if (specData?.lexusTrackingFlag === 'Y') {
            providerId =
                lexusMCLaunched || lexusTDMCLaunched ? 'MONOGRAMREYNOLDS' : 'REYNOLDS';
            siteType = 'LEXUS';
            retailerId = specData.lexusRetId;
            cookieName = '_sda:lexus:T3:session';
        } else if (specData?.mbzTrackingFlag === 'Y') {
            providerId = 'REYNOLDS';
            siteType = 'MBUSA';
            retailerId = specData.mbzRetId;
            cookieName = '_sda:mbusa:T3:session';
        } else if (specData?.mazdaCATrackingFlag === 'Y') {
            providerId = 'REYNOLDS';
            siteType = 'MAZDACA';
            retailerId = specData.mazdaCARetId;
            cookieName = '_sda:mazdaca:T3:session';
        } else if (specData?.vwTrackingFlag === 'Y') {
            providerId = 'REYNOLDS';
            siteType = 'VW';
            retailerId = specData.vwRetId;
            cookieName = '_sda:volkswagen:T3:session';
        }

        const sysType = window.location.href.slice(9, 10);
        const scriptUrl =
            sysType !== 'i' && window.location.hostname !== 'localhost' ?
                `//tagging.shiftdigitalapps.io/scripts/${siteType}/sd.js`:
                `//tagging-staging.shiftdigitalapps.io/scripts/${siteType}/sd.js`;

        return {
            scriptUrl,
            siteType,
            retailerId,
            providerId,
            cookieName,
        };
    }

    // Set the session cookie when enabled
    useEffect(() => {
        const isAnalyticsEnabled =
            props.specData?.toyotaTrackingFlag === 'Y' ||
            props.specData?.lexusTrackingFlag === 'Y' ||
            props.specData?.mbzTrackingFlag === 'Y' ||
            props.specData?.mazdaCATrackingFlag === 'Y' ||
            props.specData?.vwTrackingFlag === 'Y';

        if (!isAnalyticsEnabled || fetchingSpecs || loadedSDAnalytics.current) {
            return;
        }

        const config = getSdConfig(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
            props.lexusMCLaunched, props.lexusTDMCLaunched,
        );

        if (!config) {
            return;
        }

        SetCookie(config?.cookieName, '');

        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('_sd_event')) {
                sessionStorage.removeItem(key);
            }
        }

        if (window['sd'] && window['sd'].l) {
            loadedSDAnalytics.current = true;

            return;
        }

        window['sd'] =
            window['sd'] ||
            function (...args: any[]) {
                (window['sd'].q = window['sd'].q || []).push(args);
            };
        window['sd'].l = Date.now();

        const script = document.createElement('script');
        script.async = true;
        script.src = config?.scriptUrl;
        script.onload = () => {
            (window as any).sd(
                'create',
                config.siteType,
                config.retailerId,
                config.providerId,
            );
            loadedSDAnalytics.current = true;
            sendSdEvent('scheduleServiceImpression', {
                scheduleServiceSessionId: props.sessionId,
                scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched),
            });
        };
        document.head.appendChild(script);

        return () => {
            if (script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [fetchingSpecs, props.lexusMCLaunched, props.lexusTDMCLaunched, props.specData, props.toyotaMCLaunched,
        props.toyotaTDMCLaunched, props.sessionId]);

    useEffect(() => {
        const unloadCallback = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };

        const isServiceDocs = props.serviceDocsSpecs && (props.serviceDocsFlag === 'Y' || props.viewServiceHistFlag === 'Y');

        const currentViewPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId;

        const confirmationPane = currentViewPane === ComponentMapIds.Confirmation || currentViewPane === ComponentMapIds.ThankYou;

        if ((props.custInfo?.login || ((props.appointment.ApptCust.custFirstName !== '' || props.appointment.ApptVeh.make !== '') &&
            !isServiceDocs && !confirmationPane)) && !isPreview) {
            window.addEventListener('beforeunload', unloadCallback);
        }

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
        };
    }, [props.custInfo?.login, props.appointment.ApptCust.custFirstName, props.appointment.ApptVeh.make, props.serviceDocsFlag,
        props.serviceDocsSpecs, props.viewServiceHistFlag, props.mainPaneViewOrder, isPreview]);


    useEffect(() => {
        // use width to determine if a user is using a mobile device.
        // For now we upload to the Framework state. Later we will log this.
        // Only captures this once at the beginning so changing widths on later screens won't recall this
        if (props.logId) {
            if (document.body.clientWidth <= 600) {
                props.setDesktopOrMobile('M');
                // setLocalDesktopOrMobile('M');
            } else {
                // default to Desktop
                props.setDesktopOrMobile('D');
                // setLocalDesktopOrMobile('D');
            }
        }
    }, [props.setDesktopOrMobile, props.logId]);

    useEffect(() => history.listen((_) => {
        // Handle browser back event
        if (history.action === 'POP') {
            props.clearApptState();
            props.clearMenusState();
            props.clearNewVehicle();
            props.clearROHistory();
            props.clearRO();
            props.selectVehicle('');
            const blankCust: Customer = {
                nameId: '', firstName: '', lastName: '', emailAddress: '',
                phoneNumber: '', login: false, prefContact: '', textOptIn: '',
                stateAddress: '', streetAddress: '', zipCode: '', emailOptIn: '',
                city: '', phoneType: '', addVehNameId: '', gmRedeemAmt: '',
                encryptFirst: '', encryptLast: '', encryptEmail: '', encryptPhone: '',
                ascCustId: '',
            };
            props.onCustUpdate(blankCust);
            props.waitForLogOff();
            props.logCustOff();
            props.clearAdditionalServices();
            props.clearMaintContractData();
            props.clearCheckedWebOps();
            props.clearVehicles();
            props.clearMainPanes();
            props.setWaitOnRecall(false);
            props.setWaitOnMC(false);
            props.clearAddlTrackCustInfo();
            if (hasAnyBrandFlag || vwDeepLinkData) {
                props.setViewMode(ViewMode.Appt);
                props.clearMainPanes();
                props.pushToMainPanes({
                    viewId: ComponentMapIds.CustSearch,
                    component: ComponentMapIds.CustSearch,
                });
            } else {
                props.setViewMode(ViewMode.LandingPage);
            }
        }
    }), [history, props.clearApptState, props.clearMenusState, props.clearNewVehicle, props.clearROHistory,
        props.selectVehicle, props.onAppCustUpdate, props.clearAdditionalServices, props.onCustUpdate,
        props.clearVehicles, props.clearMainPanes, props.clearCheckedWebOps, props.appointment, props.custInfo,
        props.setWaitOnRecall, props.setViewMode, props.clearRO, props.logCustOff, props.waitForLogOff,
        props.setWaitOnMC, props.clearMaintContractData, props.pushToMainPanes, hasAnyBrandFlag, vwDeepLinkData,
        props.clearAddlTrackCustInfo,
    ]);

    useEffect(() => {
        const type = window.location.href.slice(8, 10).toUpperCase();
        const sysNo = window.location.href.slice(11, 16);
        // Set the environment
        setSysType(type === 'RD' || type === 'RI' || sysNo === 'lhost' ? 'QA' : 'Prod');
        const url = URLHelper.getDataServiceUrl();
        setApiURL(url);
    }, []);

    useEffect(() => {
        if (!fetchingSpecs && props.specData?.gmTrackingFlag) {
            const url = '//traffic.prod.cobaltgroup.com/js/cpp.js';
            let script: HTMLScriptElement | null = document.querySelector(`script[src="${url}"]`);

            if (!script) {
                // Create script
                script = document.createElement('script');
                script.src = url;
                script.async = true;
                script.setAttribute('type', 'text/javascript');
                // Add script to document body
                document.body.appendChild(script);
            }
        }

        // tagManager set up here.
        try {
            if (!fetchingSpecs && props.specData?.tagManagerId && isCookiesEnabled) {
                const tagManagerArgs = {
                    gtmId: props.specData.tagManagerId,
                    dataLayerName: 'asc_datalayer',
                };
                TagManager.initialize(tagManagerArgs);
            }
        } catch (e) {
            Sentry.captureException(e);
        }
    }, [fetchingSpecs, isCookiesEnabled, props.specData]);
    // Setup the theme switcher
    useEffect(() => {
        document.documentElement.classList.add(`theme-${props.theme}`);

        return () => {
            document.documentElement.classList.remove(`theme-${props.theme}`);
        };
    }, [props.theme]);
    useEffect(() => {
        setTokenError(false);
        // Initialize datalayer
        try {
            window['asc_datalayer'] = window['asc_datalayer'] || [];
            window['dataLayer'] = window['dataLayer'] || [];
        } catch (e) {
            Sentry.captureException(e);
        }
        // Find if the user has an existing token saved
        const tokenCookie = GetCookie('token') ?? '';
        // Get the URL and pull out the token
        const urlString = window.location;
        // const token = new URLSearchParams(urlString.search).get('token');
        const tokens = new URLSearchParams(urlString.search).getAll('token');
        // If the user has a token in the params prioritize it, otherwise use the stored token
        let token = tokens[0];
        const app = new URLSearchParams(urlString.search).getAll('app');
        // Currently, only Connected Vehicle will pass the app
        if (app[0] === 'CV') {
            setLoadApp('CV');
        } else {
            setLoadApp('SVP');
        }

        const addlTrackToken = tokens[1];
        const VIN = new URLSearchParams(urlString.search).getAll('VIN');
        const vin = new URLSearchParams(urlString.search).getAll('vin');
        const addlTrackVin = vin[0] ? vin[0] : VIN[0];
        const Variant = new URLSearchParams(urlString.search).getAll('Variant');
        const variant = new URLSearchParams(urlString.search).getAll('variant');
        const Mileage = new URLSearchParams(urlString.search).getAll('Mileage');
        const mileage = new URLSearchParams(urlString.search).getAll('mileage');
        const Provider = new URLSearchParams(urlString.search).getAll('Provider');
        const provider = new URLSearchParams(urlString.search).getAll('provider');
        const Keyword = new URLSearchParams(urlString.search).getAll('Keyword');
        const keyword = new URLSearchParams(urlString.search).getAll('keyword');
        const vwDeepLinkVariant = variant[0] ? variant[0] : Variant[0];
        const vwDeepLinkMileage = mileage[0] ? mileage[0] : Mileage[0];
        const vwDeepLinkProvider = provider[0] ? provider[0] : Provider[0];
        const vwDeepLinkKeyword = keyword[0] ? keyword[0] : Keyword[0];
        if (props.specData?.vwTrackingFlag && (vwDeepLinkMileage || vwDeepLinkVariant || vwDeepLinkProvider || vwDeepLinkKeyword)) {
            props.setVwDeepLinkMileage(vwDeepLinkMileage);
            props.setVwDeepLinkTrackData(vwDeepLinkVariant, vwDeepLinkProvider, vwDeepLinkKeyword);
        }
        let addlTrackYear = '';
        let addlTrackMake = '';
        let addlTrackModel = '';
        if (!addlTrackVin) {
            const year = new URLSearchParams(urlString.search).getAll('year');
            addlTrackYear = year[0];
            const make = new URLSearchParams(urlString.search).getAll('make');
            addlTrackMake = make[0];
            const model = new URLSearchParams(urlString.search).getAll('model');
            addlTrackModel = model[0] ? model[0].replace('+', ' ') : '';
        }
        props.setAddlTrackData(addlTrackToken, addlTrackVin, addlTrackYear, addlTrackMake, addlTrackModel);

        if (!haveToken) {
            // If the user has a mode in the params prioritize it, otherwise use the stored token
            const modeCookie: string = GetCookie('mode') ?? '';
            let mode = new URLSearchParams(urlString.search).get('mode') ?? '';
            const localURL = urlString.host ?? '';
            let localURLFlag = false;
            if (localURL === 'localhost:3000') {
                localURLFlag = true;
            }
            if (token && token !== tokenCookie) {
                if (tokenCookie) {
                    props.setPreviousToken(tokenCookie);
                }
                document.cookie = `token=${token};SameSite=None;secure`;
                SetCookie('mode', mode);
            } else {
                token = tokenCookie;
                mode = modeCookie;
            }
            props.getTokenData(token!, localURLFlag);
            setInitLang(false);
            setHaveToken(true);
            props.setPreviewFlag(mode!);
        }
    }, [props.getTokenData, props.setPreviewFlag, props.setAddlTrackData, props.setPreviousToken,
        hasAnyBrandFlag, haveToken, props.setVwDeepLinkMileage, props.setVwDeepLinkTrackData, props.specData?.vwTrackingFlag]);

    // Give error if a tokenErrMsg
    useEffect(() => {
        if (props.tokenErrMsg && props.tokenErrMsg !== '') {
            setFetchingSpecs(false);
            setTokenError(true);
            if (props.tokenErrMsg === '1000') {
                setTokenErrorMsg(strings.InvalidTokenError);
            }
            if (props.tokenErrMsg === '1001') {
                setTokenErrorMsg(strings.InvalidTokenError);
            }
        }
    }, [setTokenError, setFetchingSpecs, props.tokenErrMsg]);
    // We cannot get specs until we have the token data returned

    // Get specs for the application
    useEffect(() => {
        if (props.specProfile !== '' && !specFlag && props.specProfile !== undefined && !props.serviceDocsFlag &&
        !props.viewServiceHistFlag && props.portalLaunched === 'Y' && !props.tlmId) {
            props.getSpecs(props.specProfile, props.store, props.branch);
            props.setViewMode(ViewMode.LandingPage);
            // history.push(window.location.search);
            // history.push('/' + window.location.search);
            setSpecFlag(true);
            let language = props.locale;
            if (!initLang) {
                if (props.countryCode === 'CA') {
                    if (props.languageCode === 'FC') {
                        language = 'fr-CA';
                    } else {
                        language = 'en-CA';
                    }
                }
                setLocale(language);
                props.toggleLocale(language);
                setInitLang(true);
            }
        } else {
            if (props.tlmId) {
                setFetchingSpecs(false);
            }
        }
    }, [props.getSpecs, props.specProfile, props.store, props.branch, props.setViewMode,
        props.locale, props.toggleLocale, props.countryCode, props.languageCode, initLang, setInitLang,
        specFlag, setSpecFlag, props.getServiceDocsSpecs, props.serviceDocsFlag, props.viewServiceHistFlag,
        props.portalLaunched, props.tlmId]);

    useEffect(() => {
        if (props.specData?.mobileName){
            setProductName(props.specData?.mobileName);
        }
    }, [props.specData]);

    useEffect(() => {
        if (props.osdLaunched === 'Y' && !docSpecFlag && !props.tlmId) {
            props.getServiceDocsSpecs(props.store, props.branch);
            let language = props.locale;
            if (!initLang) {
                if (props.countryCode === 'CA') {
                    if (props.languageCode === 'FC') {
                        language = 'fr-CA';
                    } else {
                        language = 'en-CA';
                    }
                }
                setLocale(language);
                props.toggleLocale(language);
                setInitLang(true);
            }
            setDocSpecFlag(true);
        }
    }, [initLang,
        docSpecFlag,
        props.osdLaunched,
        props.store,
        props.branch,
        props.languageCode,
        props.countryCode,
        props.locale,
        props.getServiceDocsSpecs,
        props.toggleLocale,
        props.tlmId,
    ]);

    useEffect(() => {
        if (props.frameworkTealiumAccount) {
            const profile = 'reynoldsservice';
            // Default environment to prod
            let environment = 'prod';
            // check for qa environment
            const type = window.location.href.slice(8, 10).toUpperCase();
            if (type === 'RD' || type === 'RI') {
                environment = 'qa';
            }
            // check for dev environment (localhost)
            const sysNum = window.location.href.slice(10, 16);
            if (sysNum === 'alhost') {
                environment = 'dev';
            }
            const account = props.frameworkTealiumAccount;
            const d = document.createElement('script');
            d.src = `//tags.tiqcdn.com/utag/${account}/${profile}/${environment}/utag.js`;
            d.type = 'text/javascript';
            d.async = true;
            const a = document.getElementsByTagName('body')[0];
            a?.parentNode?.insertBefore(d, a);
        }
    }, [props.frameworkTealiumAccount]);

    // Based on specs set the active theme.
    useEffect(() => {
        // Find if the user has a saved colorSensitive Flag
        const colorSensitiveCookie = GetCookie('colorSensitive') ?? '';
        if (props.specData) {
            setFetchingSpecs(false);
            if (props.serviceDocsSpecs &&
                props.serviceDocsSpecs.osdProfile &&
                (props.serviceDocsFlag || props.viewServiceHistFlag)) {
                // When using the ServiceDocs specProfile, serviceDocsSpecs sets the appearance.
                const theme = props.serviceDocsSpecs.theme ? props.serviceDocsSpecs.theme:'D';
                const favIconPath = getFavIconPath(theme);
                const titleName = props.serviceDocsSpecs.titleTag ?? strings.ServiceDocsDefaultTitle;
                // Set the document title to titleName.
                document.title = titleName;
                // Initialize nodes
                const shortcutIcon: HTMLLinkElement =
                    document.querySelector('link[id="icon"]') ||
                    document.querySelector('link[id="shortcut icon"]') ||
                    document.createElement('link');
                const appleTouchIcon: HTMLLinkElement =
                    document.querySelector('link[id="apple touch icon"]') ||
                    document.createElement('link');
                // Set the favicon
                if (props.serviceDocsSpecs.faviconURL) {
                    // Use the favicon override if it exists.
                    shortcutIcon.href = props.serviceDocsSpecs.faviconURL;
                    appleTouchIcon.href = props.serviceDocsSpecs.faviconURL;
                } else {
                    // Otherwise use the make favicon from the theme.
                    shortcutIcon.href = favIconPath;
                    shortcutIcon.rel = 'icon';
                    appleTouchIcon.href = favIconPath;
                    appleTouchIcon.rel = 'apple-touch-icon';
                }
                document.getElementsByTagName('head')[0].appendChild(shortcutIcon);
                document.getElementsByTagName('head')[0].appendChild(appleTouchIcon);
                // Override the 32x32 shortcut icon
                let link: HTMLLinkElement = document.querySelector('link[id="32x32 shortcut icon"]') ||
                document.createElement('link');
                if (props.serviceDocsSpecs.faviconURL) {
                    link.href = props.serviceDocsSpecs.faviconURL;
                    document.head.appendChild(link);
                } else if (props.specData.oemTheme && favIconPath) {
                    link.href = favIconPath;
                    document.head.appendChild(link);
                }
                // Override the 114x114 apple-touch-icon (iPhone and iPod Touch (Retina) Home Screen Icon)
                link = document.querySelector('link[id="114x114 apple touch icon"]') ||
                document.createElement('link');
                let icon114x114 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[0] && props.specData.homeScrnIcnActv[0]) {
                    link.href = props.specData.homeScrnIcon[0];
                    document.head.appendChild(link);
                    icon114x114 = props.specData.homeScrnIcon[0];
                } else if (props.serviceDocsSpecs.faviconURL) {
                    link.href = props.serviceDocsSpecs.faviconURL;
                    document.head.appendChild(link);
                    icon114x114 = props.serviceDocsSpecs.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon114x114 = favIconPath;
                }
                // Override the 57x57 apple-touch-icon (iPhone and iPod Touch (Non Retina) Home Screen Icon)
                link = document.querySelector('link[id="57x57 apple touch icon"]') ||
                document.createElement('link');
                let icon57x57 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[1] && props.specData.homeScrnIcnActv[1]) {
                    link.href = props.specData.homeScrnIcon[1];
                    document.head.appendChild(link);
                    icon57x57 = props.specData.homeScrnIcon[1];
                } else if (props.serviceDocsSpecs.faviconURL) {
                    link.href = props.serviceDocsSpecs.faviconURL;
                    document.head.appendChild(link);
                    icon57x57 = props.serviceDocsSpecs.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon57x57 = favIconPath;
                }
                // Override the 144x144 apple-touch-icon (iPad (Retina) Home Screen Icon)
                link = document.querySelector('link[id="144x144 apple touch icon"]') ||
                document.createElement('link');
                let icon144x144 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[2] && props.specData.homeScrnIcnActv[2]) {
                    link.href = props.specData.homeScrnIcon[2];
                    document.head.appendChild(link);
                    icon144x144 = props.specData.homeScrnIcon[2];
                } else if (props.serviceDocsSpecs.faviconURL) {
                    link.href = props.serviceDocsSpecs.faviconURL;
                    document.head.appendChild(link);
                    icon144x144 = props.serviceDocsSpecs.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon144x144 = favIconPath;
                }
                // Override the 72x72 apple-touch-icon (iPad (Non Retina) Home Screen Icon)
                link = document.querySelector('link[id="72x72 apple touch icon"]') ||
                document.createElement('link');
                let icon72x72 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[3] && props.specData.homeScrnIcnActv[3]) {
                    link.href = props.specData.homeScrnIcon[3];
                    document.head.appendChild(link);
                    icon72x72 = props.specData.homeScrnIcon[3];
                } else if (props.serviceDocsSpecs.faviconURL) {
                    link.href = props.serviceDocsSpecs.faviconURL;
                    document.head.appendChild(link);
                    icon72x72 = props.serviceDocsSpecs.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon72x72 = favIconPath;
                }
                // Override the 192x192 icon (Android Home Screen Icon)
                link = document.querySelector('link[id="192x192 icon"]') ||
                document.createElement('link');
                let icon192x192 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[4] && props.specData.homeScrnIcnActv[4]) {
                    link.href = props.specData.homeScrnIcon[4];
                    document.head.appendChild(link);
                    icon192x192 = props.specData.homeScrnIcon[4];
                } else if (props.serviceDocsSpecs.faviconURL) {
                    link.href = props.serviceDocsSpecs.faviconURL;
                    document.head.appendChild(link);
                    icon192x192 = props.serviceDocsSpecs.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon192x192 = favIconPath;
                }
                const dynamicManifest = {
                    'short_name': document.title,
                    'name': document.title,
                    'icons': [
                        {
                            'src': icon114x114,
                            'sizes': '114x114',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon57x57,
                            'sizes': '57x57',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon72x72,
                            'sizes': '72x72',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon144x144,
                            'sizes': '144x144',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon192x192,
                            'sizes': '16x16 24x24 32x32 64x64 192x192',
                            'type': 'image/png image/x-icon',
                            'purpose': 'any maskable',
                        },
                    ],
                    'start_url': './index.html',
                    'display': 'standalone',
                    'theme_color': '#000000',
                    'background_color': '#ffffff',
                };
                const stringManifest = JSON.stringify(dynamicManifest);
                const blob = new Blob([stringManifest], {type: 'application/json'});
                const manifestUrl = URL.createObjectURL(blob);
                document.querySelector('link[rel="manifest"]')?.setAttribute('href', manifestUrl);
                // Update theme
                if (colorSensitiveCookie !== 'colorSensitive') {
                    props.updateTheme(checkSpecTheme(theme));
                } else {
                    props.updateTheme('colorSensitive');
                }
            } else {
                // titleName should come from specs.  If it is blank then set it to strings.ServicePortal.
                let titleName = props.specData?.titleTag;
                if (titleName === '' || titleName === undefined) {
                    titleName = strings.ServicePortal;
                }
                // Set the document title to titleName
                document.title = titleName;
                // Set the Favicon if one was passed in via specs
                const favIconPath = getFavIconPath(props.specData.oemTheme);
                // let iconDefault = '%HOST_URL%/era.ico';
                if (props.specData.faviconURL) {
                    const link: HTMLLinkElement = document.querySelector('link[id="icon"]') ||
                    document.querySelector('link[id="shortcut icon"]') || document.createElement('link');
                    link.href = props.specData.faviconURL;
                    document.getElementsByTagName('head')[0].appendChild(link);
                    const applelink: HTMLLinkElement =
                        document.querySelector('link[id="apple touch icon"]') || document.createElement('link');
                    applelink.href = props.specData.faviconURL;
                    document.getElementsByTagName('head')[0].appendChild(applelink);
                    // iconDefault = props.specData.faviconURL;
                } else if (props.specData.oemTheme) {
                    // If we don't have an favicon override use the make favicon
                    // const favIconPath = getFavIconPath(props.specData.oemTheme);
                    if (favIconPath) {
                        const link: HTMLLinkElement = document.querySelector('link[id="icon"]') ||
                        document.querySelector('link[id="shortcut icon"]') || document.createElement('link');
                        link.href = favIconPath;
                        link.rel = 'icon';
                        document.head.appendChild(link);
                        const applelink: HTMLLinkElement =
                            document.querySelector('link[id="apple touch icon"]') || document.createElement('link');
                        applelink.href = favIconPath;
                        applelink.rel = 'apple-touch-icon';
                        document.head.appendChild(applelink);
                        // iconDefault = favIconPath;
                    }
                }
                // Override the 32x32 shortcut icon
                let link: HTMLLinkElement = document.querySelector('link[id="32x32 shortcut icon"]') ||
                document.createElement('link');
                // let icon32x32 = '%HOST_URL%/era.ico';
                if (props.specData.faviconURL) {
                    link.href = props.specData.faviconURL;
                    document.head.appendChild(link);
                    // icon32x32 = props.specData.faviconURL;
                } else if (props.specData.oemTheme && favIconPath) {
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    // icon32x32 = favIconPath;
                }
                // Override the 114x114 apple-touch-icon (iPhone and iPod Touch (Retina) Home Screen Icon)
                link = document.querySelector('link[id="114x114 apple touch icon"]') ||
                document.createElement('link');
                let icon114x114 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[0] && props.specData.homeScrnIcnActv[0]) {
                    link.href = props.specData.homeScrnIcon[0];
                    document.head.appendChild(link);
                    icon114x114 = props.specData.homeScrnIcon[0];
                } else if (props.specData.faviconURL) {
                    link.href = props.specData.faviconURL;
                    document.head.appendChild(link);
                    icon114x114 = props.specData.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon114x114 = favIconPath;
                }
                // Override the 57x57 apple-touch-icon (iPhone and iPod Touch (Non Retina) Home Screen Icon)
                link = document.querySelector('link[id="57x57 apple touch icon"]') ||
                document.createElement('link');
                let icon57x57 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[1] && props.specData.homeScrnIcnActv[1]) {
                    link.href = props.specData.homeScrnIcon[1];
                    document.head.appendChild(link);
                    icon57x57 = props.specData.homeScrnIcon[1];
                } else if (props.specData.faviconURL) {
                    link.href = props.specData.faviconURL;
                    document.head.appendChild(link);
                    icon57x57 = props.specData.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon57x57 = favIconPath;
                }
                // Override the 144x144 apple-touch-icon (iPad (Retina) Home Screen Icon)
                link = document.querySelector('link[id="144x144 apple touch icon"]') ||
                document.createElement('link');
                let icon144x144 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[2] && props.specData.homeScrnIcnActv[2]) {
                    link.href = props.specData.homeScrnIcon[2];
                    document.head.appendChild(link);
                    icon144x144 = props.specData.homeScrnIcon[2];
                } else if (props.specData.faviconURL) {
                    link.href = props.specData.faviconURL;
                    document.head.appendChild(link);
                    icon144x144 = props.specData.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon144x144 = favIconPath;
                }
                // Override the 72x72 apple-touch-icon (iPad (Non Retina) Home Screen Icon)
                link = document.querySelector('link[id="72x72 apple touch icon"]') ||
                document.createElement('link');
                let icon72x72 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[3] && props.specData.homeScrnIcnActv[3]) {
                    link.href = props.specData.homeScrnIcon[3];
                    document.head.appendChild(link);
                    icon72x72 = props.specData.homeScrnIcon[3];
                } else if (props.specData.faviconURL) {
                    link.href = props.specData.faviconURL;
                    document.head.appendChild(link);
                    icon72x72 = props.specData.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon72x72 = favIconPath;
                }
                // Override the 192x192 icon (Android Home Screen Icon)
                link = document.querySelector('link[id="192x192 icon"]') ||
                document.createElement('link');
                let icon192x192 = getFavIconPath('D');
                if (props.specData.homeScrnIcon && props.specData.homeScrnIcon[4] && props.specData.homeScrnIcnActv[4]) {
                    link.href = props.specData.homeScrnIcon[4];
                    document.head.appendChild(link);
                    icon192x192 = props.specData.homeScrnIcon[4];
                } else if (props.specData.faviconURL) {
                    link.href = props.specData.faviconURL;
                    document.head.appendChild(link);
                    icon192x192 = props.specData.faviconURL;
                } else if (props.specData.oemTheme && favIconPath){
                    link.href = favIconPath;
                    document.head.appendChild(link);
                    icon192x192 = favIconPath;
                }
                // Override the manifest
                const dynamicManifest = {
                    'short_name': document.title,
                    'name': document.title,
                    'icons': [
                        // {
                        //     'src': iconDefault,
                        //     'type': 'image/png',
                        //     'purpose': 'any maskable',
                        // },
                        // {
                        //     'src': icon32x32,
                        //     'sizes': '32x32',
                        //     'type': 'image/png',
                        //     'purpose': 'any maskable',
                        // },
                        {
                            'src': icon114x114,
                            'sizes': '114x114',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon57x57,
                            'sizes': '57x57',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon72x72,
                            'sizes': '72x72',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon144x144,
                            'sizes': '144x144',
                            'type': 'image/png',
                            'purpose': 'any maskable',
                        },
                        {
                            'src': icon192x192,
                            'sizes': '16x16 24x24 32x32 64x64 192x192',
                            'type': 'image/png image/x-icon',
                            'purpose': 'any maskable',
                        },
                    ],
                    'start_url': './index.html',
                    'display': 'standalone',
                    'theme_color': '#000000',
                    'background_color': '#ffffff',
                };
                const stringManifest = JSON.stringify(dynamicManifest);
                const blob = new Blob([stringManifest], {type: 'application/json'});
                const manifestUrl = URL.createObjectURL(blob);
                document.querySelector('link[rel="manifest"]')?.setAttribute('href', manifestUrl);
                // Update theme
                if (props.frameworkError !== '8000') {
                    if (colorSensitiveCookie !== 'colorSensitive') {
                        props.updateTheme(checkSpecTheme(props.specData.oemTheme));
                    } else {
                        props.updateTheme('colorSensitive');
                    }
                }
            }
            // if Service Docs and useProfile set the theme
            if (props.serviceDocsSpecs && (props.serviceDocsFlag || props.viewServiceHistFlag) &&
            props.serviceDocsSpecs.useProfile) {
                const theme = props.serviceDocsSpecs.theme ? props.serviceDocsSpecs.theme:'D';
                if (colorSensitiveCookie !== 'colorSensitive') {
                    props.updateTheme(checkSpecTheme(theme));
                } else {
                    props.updateTheme('colorSensitive');
                }
            }
        } else {
            setFetchingSpecs(true);
        }
    }, [props.specData,
        props.serviceDocsSpecs,
        props.serviceDocsFlag,
        props.viewServiceHistFlag,
        props.updateTheme,
        props.specProfile,
        props.frameworkError]);

    // Display a loading circle and prevent user from leaving screen until
    // recalls call is complete
    useEffect(() => {
        if (props.waitOnRecall) {
            setFetchingRecalls(true);
        } else {
            setFetchingRecalls(false);
        }
    }, [props.recallData.inquiryReturned,
        props.inquiryInvoked,
        props.recallData.inquiryError,
        props.waitOnRecall]);

    // Display a loading circle and prevent user from leaving screen until
    // recalls call is complete
    useEffect(() => {
        if (props.waitOnMC) {
            setFetchingMC(true);
        } else {
            setFetchingMC(false);
        }
    }, [props.MCData.MCReturned,
        props.inquiryInvoked,
        props.MCData.MCError,
        props.waitOnMC]);

    // Display a loading circle and prevent user from leaving screen until
    // customer is logged off and call is complete
    useEffect(() => {
        if (props.loggingCustOff) {
            setLoggingOffCust(true);
        } else {
            setLoggingOffCust(false);
        }
    }, [props.loggingCustOff]);

    const handleClose = () => {
        // If they close the error message, reload the page
        history.go(0);
    };

    let router = (
        <BrowserRouter basename={process.env.HOST_URL}>
            <Switch>
                <Route
                    path='/'
                    component={Main}
                    exact
                />
            </Switch>
        </BrowserRouter>
    );

    const connectedVehicle = (
        <BrowserRouter basename={process.env.HOST_URL}>
            <Switch>
                <Route
                    path='/'
                    component={ConnectedVehicle}
                    exact
                />
            </Switch>
        </BrowserRouter>
    );

    if (tokenError) {
        router = <div className={css.tokenError}>{tokenErrorMsg}</div>;
    } else if (props.eraError) {
        router = (
            <ErrorMessage
                headerMsg={strings.Oops}
                errorMsg={strings.NetworkError}
                primaryBtnMessage={strings.Okay}
                secondaryBtnMessage=''
                primaryHandler={handleClose}
                secondaryHandler={() => null}
                closeHandler={handleClose}
            />
        );
    } else if (
        props.custError === '8000' ||
        props.decodeErr === '8000' ||
        props.servicesError === '8000' ||
        props.recallError === '8000' ||
        props.appointment.error?.errorCode === '8000' ||
        props.quickTimesError === '8000' ||
        props.rmTimesError === '8000' ||
        props.menuError === '8000' ||
        props.vehError === '8000' ||
        props.serviceDocsError === '8000' ||
        props.webOpsError === '8000' ||
        props.frameworkError === '8000' ||
        props.repairOrderError === '8000' ||
        props.roHistoryError === '8000') {
        router = (<ErrorMessage
            headerMsg={strings.InvalidFunction}
            errorMsg={strings.PleaseRefresh}
            primaryBtnMessage={strings.Okay}
            secondaryBtnMessage={''}
            primaryHandler={handleClose}
            secondaryHandler={() => null}
            closeHandler={handleClose}
        />);
    }

    const currentViewPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId;

    return (
        <ErrorBoundary application={name} environment={sysType} version={version} apiURL={apiUrl}>
            <ThemeProvider value={themeBase}>
                {fetchingSpecs || fetchingRecalls || loggingOffCust || fetchingMC || (props.loading === 'pending' &&
                    currentViewPane !== ComponentMapIds.ServiceDocs && currentViewPane !== ComponentMapIds.LandingPage) ?
                    <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
                {(loadApp === 'SVP') || tokenError ? router : null}
                {(loadApp === 'CV') && props.tlmId ? connectedVehicle : null}
                {(loadApp === 'CV') ? null :
                    <iframe title='hidden' name={'hidden-iframe'} className={css.hiddenIframe}></iframe>}
            </ThemeProvider>
        </ErrorBoundary>
    );
};

export default App;
